import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Bold, Italic, Underline, Link, Smile } from 'lucide-react';
import { RichTextContent, RichTextRun, RichTextFormat } from '../../types/good-things';
import EmojiPicker, { EmojiClickData, Theme } from 'emoji-picker-react';
import '../../styles/emoji-picker.css';

interface RichTextEditorProps {
  value: RichTextContent;
  onChange: (value: RichTextContent) => void;
  placeholder?: string;
  className?: string;
  required?: boolean;
}

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = "Enter the good thing note...",
  className = "",
  required = true
}) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');
  const [selectedRange, setSelectedRange] = useState<Range | null>(null);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [currentFormat, setCurrentFormat] = useState<RichTextFormat>({});
  const buttonRef = useRef<HTMLButtonElement>(null);
  const pickerContainerRef = useRef<HTMLDivElement>(null);

  // Handle dark mode
  useEffect(() => {
    const darkModeQuery = window.matchMedia('(prefers-color-scheme: dark)');
    setIsDarkMode(darkModeQuery.matches);

    const handler = (e: MediaQueryListEvent) => setIsDarkMode(e.matches);
    darkModeQuery.addEventListener('change', handler);
    return () => darkModeQuery.removeEventListener('change', handler);
  }, []);

  // Handle clicks outside of emoji picker
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const pickerContainer = pickerContainerRef.current;
      const button = buttonRef.current;

      if (pickerContainer && button && 
          !pickerContainer.contains(target) && 
          !button.contains(target)) {
        setShowEmojiPicker(false);
      }
    };

    if (showEmojiPicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showEmojiPicker]);

  // Initialize editor content
  useEffect(() => {
    if (editorRef.current) {
      if (value.runs.length > 0) {
        renderContentToEditor();
      } else {
        // Clear the editor content when there are no runs
        editorRef.current.innerHTML = '';
      }
    }
  }, [value]);

  const renderContentToEditor = () => {
    if (!editorRef.current) return;

    // Store current cursor position BEFORE rebuilding DOM
    const selection = window.getSelection();
    let cursorPosition = 0;
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      const preCaretRange = range.cloneRange();
      preCaretRange.selectNodeContents(editorRef.current);
      preCaretRange.setEnd(range.endContainer, range.endOffset);
      cursorPosition = preCaretRange.toString().length;
    }

    const editor = editorRef.current;
    editor.innerHTML = '';

    value.runs.forEach(run => {
      // Handle line breaks by splitting text and creating br elements
      const textParts = run.text.split('\n');
      
      textParts.forEach((textPart, index) => {
        if (index > 0) {
          // Add line break before each part except the first
          editor.appendChild(document.createElement('br'));
        }
        
        if (textPart) { // Only create span if there's actual text
          if (run.format && run.format.link) {
            const link = document.createElement('a');
            link.href = run.format.link.url;
            link.textContent = textPart;
            link.target = '_blank';
            link.rel = 'noopener noreferrer';
            link.className = 'text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300';
            if (run.format.bold) link.style.fontWeight = 'bold';
            if (run.format.italic) link.style.fontStyle = 'italic';
            if (run.format.underline) link.style.textDecoration = 'underline';
            editor.appendChild(link);
          } else {
            const span = document.createElement('span');
            span.textContent = textPart;
            
            if (run.format) {
              if (run.format.bold) span.style.fontWeight = 'bold';
              if (run.format.italic) span.style.fontStyle = 'italic';
              if (run.format.underline) span.style.textDecoration = 'underline';
            }
            
            editor.appendChild(span);
          }
        }
      });
    });

    // Restore cursor position AFTER rebuilding DOM
    if (cursorPosition > 0) {
      const walker = document.createTreeWalker(
        editor,
        NodeFilter.SHOW_TEXT,
        null
      );
      
      let currentPos = 0;
      let node;
      while (node = walker.nextNode()) {
        const textLength = node.textContent?.length || 0;
        if (currentPos + textLength >= cursorPosition) {
          const range = document.createRange();
          const selection = window.getSelection();
          const offset = cursorPosition - currentPos;
          range.setStart(node, Math.min(offset, textLength));
          range.setEnd(node, Math.min(offset, textLength));
          if (selection) {
            selection.removeAllRanges();
            selection.addRange(range);
          }
          break;
        }
        currentPos += textLength;
      }
    }
  };

  const parseEditorContent = (): RichTextContent => {
    if (!editorRef.current) {
      return { runs: [{ text: '' }], plainText: '' };
    }

    const editor = editorRef.current;
    const plainText = editor.textContent || '';
    
    // Use the utility function to parse HTML to rich text
    const htmlContent = editor.innerHTML;
    
    // Import the parsing function from utils
    const parseHTMLToRichText = (html: string): RichTextContent => {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;

      const runs: RichTextRun[] = [];
      
      const parseNode = (node: Node, inheritedFormat: RichTextFormat = {}) => {
        if (node.nodeType === Node.TEXT_NODE) {
          const text = node.textContent || '';
          if (text) { // Include all text, including whitespace
            runs.push({
              text,
              format: Object.keys(inheritedFormat).length > 0 ? { ...inheritedFormat } : undefined
            });
          }
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          const element = node as HTMLElement;
          const newFormat = { ...inheritedFormat };
          
          // Parse formatting from element tags
          if (element.tagName === 'B' || element.tagName === 'STRONG') {
            newFormat.bold = true;
          }
          if (element.tagName === 'I' || element.tagName === 'EM') {
            newFormat.italic = true;
          }
          if (element.tagName === 'U') {
            newFormat.underline = true;
          }
          if (element.tagName === 'A') {
            const href = element.getAttribute('href');
            if (href) {
              newFormat.link = { url: href };
            }
          }
          
          // Handle line breaks
          if (element.tagName === 'BR') {
            runs.push({
              text: '\n',
              format: Object.keys(inheritedFormat).length > 0 ? { ...inheritedFormat } : undefined
            });
            return;
          }
          
          // Handle span elements with style-based formatting
          if (element.tagName === 'SPAN') {
            const computedStyle = window.getComputedStyle(element);
            if (computedStyle.fontWeight === 'bold' || computedStyle.fontWeight === '700') {
              newFormat.bold = true;
            }
            if (computedStyle.fontStyle === 'italic') {
              newFormat.italic = true;
            }
            if (computedStyle.textDecoration?.includes('underline')) {
              newFormat.underline = true;
            }
          }
          
          // Recursively parse children
          for (const child of Array.from(node.childNodes)) {
            parseNode(child, newFormat);
          }
        }
      };

      parseNode(tempDiv);
      
      // Optimize runs by merging consecutive runs with the same formatting
      const optimizedRuns: RichTextRun[] = [];
      let currentRun: RichTextRun | null = null;
      
      for (const run of runs) {
        if (currentRun && JSON.stringify(currentRun.format) === JSON.stringify(run.format)) {
          // Merge with current run
          currentRun.text += run.text;
        } else {
          // Start new run
          if (currentRun) {
            optimizedRuns.push(currentRun);
          }
          currentRun = { ...run };
        }
      }
      
      if (currentRun) {
        optimizedRuns.push(currentRun);
      }
      
      const result = {
        runs: optimizedRuns.length > 0 ? optimizedRuns : [{ text: plainText }],
        plainText
      };
      
      return result;
    };

    return parseHTMLToRichText(htmlContent);
  };

  const handleInput = () => {
    const newContent = parseEditorContent();
    onChange(newContent);
  };

  const applyFormat = (formatType: keyof RichTextFormat) => {
    if (!editorRef.current) return;

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);
    if (range.collapsed) return; // No text selected

    let command = '';
    switch (formatType) {
      case 'bold':
        command = 'bold';
        break;
      case 'italic':
        command = 'italic';
        break;
      case 'underline':
        command = 'underline';
        break;
    }

    if (command) {
      document.execCommand(command, false);
      handleInput();
    }
  };

  const applyLink = () => {
    if (!editorRef.current) return;

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);
    if (range.collapsed) return;

    // Store the selected text and range
    setLinkText(range.toString());
    setSelectedRange(range.cloneRange());
    setLinkUrl('');
    setShowLinkModal(true);
  };

  const handleLinkSubmit = () => {
    if (!selectedRange || !linkUrl.trim()) return;

    // Restore the selection
    const selection = window.getSelection();
    if (selection) {
      selection.removeAllRanges();
      selection.addRange(selectedRange);
      
      // Create the link
      document.execCommand('createLink', false, linkUrl);
      
      // Add styling to the created link
      if (editorRef.current) {
        const links = editorRef.current.querySelectorAll('a');
        const lastLink = links[links.length - 1];
        if (lastLink) {
          lastLink.target = '_blank';
          lastLink.rel = 'noopener noreferrer';
          lastLink.className = 'text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300';
        }
      }
      
      handleInput();
    }

    // Close modal and reset state
    setShowLinkModal(false);
    setLinkUrl('');
    setLinkText('');
    setSelectedRange(null);
  };

  const handleLinkCancel = () => {
    setShowLinkModal(false);
    setLinkUrl('');
    setLinkText('');
    setSelectedRange(null);
  };

  const handleEmojiClick = (emojiData: EmojiClickData) => {
    if (!editorRef.current) return;

    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      range.deleteContents();
      range.insertNode(document.createTextNode(emojiData.emoji));
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);
    } else {
      // If no selection, append to end
      editorRef.current.textContent += emojiData.emoji;
    }

    handleInput();
    setShowEmojiPicker(false);
  };

  const toggleEmojiPicker = () => {
    setShowEmojiPicker(!showEmojiPicker);
  };

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium dark:text-gray-200">Note</label>
      
      {/* Formatting Toolbar */}
      <div className="flex items-center gap-1 p-2 border border-gray-300 dark:border-gray-600 rounded-t-md bg-gray-50 dark:bg-gray-700">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => applyFormat('bold')}
          className="h-8 w-8 p-0 hover:bg-gray-200 dark:hover:bg-gray-600"
          title="Bold (Ctrl+B)"
          tabIndex={-1}
        >
          <Bold className="h-4 w-4" />
        </Button>
        
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => applyFormat('italic')}
          className="h-8 w-8 p-0 hover:bg-gray-200 dark:hover:bg-gray-600"
          title="Italic (Ctrl+I)"
          tabIndex={-1}
        >
          <Italic className="h-4 w-4" />
        </Button>
        
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => applyFormat('underline')}
          className="h-8 w-8 p-0 hover:bg-gray-200 dark:hover:bg-gray-600"
          title="Underline (Ctrl+U)"
          tabIndex={-1}
        >
          <Underline className="h-4 w-4" />
        </Button>
        
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={applyLink}
          className="h-8 w-8 p-0 hover:bg-gray-200 dark:hover:bg-gray-600"
          title="Add Link"
          tabIndex={-1}
        >
          <Link className="h-4 w-4" />
        </Button>

        <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1" />
        
        <Button
          type="button"
          variant="ghost"
          size="sm"
          ref={buttonRef}
          onClick={toggleEmojiPicker}
          className="h-8 w-8 p-0 hover:bg-gray-200 dark:hover:bg-gray-600"
          title="Insert emoji"
          tabIndex={-1}
        >
          <Smile className="h-4 w-4" />
        </Button>
      </div>

      {/* Editor */}
      <div className="relative">
        <div
          ref={editorRef}
          contentEditable
          dir="ltr"
          onInput={handleInput}
          className={`rich-text-editor min-h-[200px] p-3 border border-gray-300 dark:border-gray-600 rounded-b-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 resize-none ${className}`}
          style={{
            direction: 'ltr',
            textAlign: 'left',
            whiteSpace: 'pre-wrap',
            wordWrap: 'break-word',
            unicodeBidi: 'embed'
          }}
          data-placeholder={placeholder}
          suppressContentEditableWarning={true}
        />
        
        {/* Placeholder styling is handled via CSS classes */}
        <style dangerouslySetInnerHTML={{
          __html: `
            .rich-text-editor[contenteditable]:empty:before {
              content: attr(data-placeholder);
              color: #9ca3af;
              pointer-events: none;
            }
            .dark .rich-text-editor[contenteditable]:empty:before {
              color: #6b7280;
            }
          `
        }} />

        {/* Emoji Picker */}
        {showEmojiPicker && (
          <div className="emoji-picker-wrapper" ref={pickerContainerRef}>
            <div className="emoji-picker-container">
              <EmojiPicker
                onEmojiClick={handleEmojiClick}
                autoFocusSearch={false}
                searchDisabled
                theme={isDarkMode ? Theme.DARK : Theme.LIGHT}
                height={350}
                width="100%"
                previewConfig={{
                  showPreview: false
                }}
                skinTonesDisabled
              />
            </div>
          </div>
        )}
      </div>

      {/* Link Modal */}
      <Dialog open={showLinkModal} onOpenChange={setShowLinkModal}>
        <DialogContent className="sm:max-w-[425px] dark:bg-gray-800 dark:border-gray-700">
          <DialogHeader>
            <DialogTitle className="dark:text-white">Add Hyperlink</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium dark:text-gray-200">Selected Text:</label>
              <div className="p-2 bg-gray-50 dark:bg-gray-700 rounded border text-sm dark:text-gray-300">
                {linkText}
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium dark:text-gray-200">URL:</label>
              <Input
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                placeholder="https://example.com"
                className="dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                autoFocus
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleLinkCancel}
              className="dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:hover:bg-gray-600"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleLinkSubmit}
              disabled={!linkUrl.trim()}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Add Link
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RichTextEditor;